import { translate } from '@/utils/translation';

// Max size in kb
/**
 * A function to validate the maximum file size for an array of files.
 *
 * @function
 * @name maxFileSize
 * @param {number} maxSize - The maximum total size allowed for all files combined, in kilobytes.
 * @param {number} [maxSizeEach] - The maximum size allowed for each individual file, in kilobytes.
 * @returns {Function} - A function that takes an array of File objects and returns a boolean indicating
 * whether the total file size is within the allowed limit, or a translated error message if the limit is exceeded.
 *
 * @example
 * const validate = maxFileSize(5000, 1000);
 * const files = [file1, file2];
 * const isValid = validate(files);
 * if (isValid !== true) {
 *   console.error(isValid); // Output: 'file_size_exceeds_limit' or 'total_file_size_exceeds_limit'
 * }
 */
export function maxFileSize(maxSize: number, maxSizeEach?: number) {
  // `size` property is in bytes so we multiply `maxSize` by 1000 to convert kilobytes to bytes
  const maxSizeInBytes = maxSize * 1000;
  const maxSizeEachInBytes = (maxSizeEach || 0) * 1000;

  return (value: File[] = []) => {
    const totalFileSizeInBytes = value?.reduce((prev, current) => prev + current.size, 0) || 0;

    if (maxSizeEach) {
      if (value?.some((file) => file.size > maxSizeEachInBytes)) {
        return translate('file_size_exceeds_limit');
      }
    }

    return totalFileSizeInBytes < maxSizeInBytes || translate('total_file_size_exceeds_limit');
  };
}
